import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { inspectionApi as api } from "../inspection/api";
import { TierBadge } from "../inspection/TierBadge";
import { FinanceCard, LinesCard, CompareCard, ComplianceCard } from "./HubCards";
import "./renderhub.css";

const roomsOf = (hub) => [...new Map(hub.before.map((b) => [b.room_key, b.room])).entries()];

const BeforeAfter = ({ hub, admin, busyRoom, onRender }) => (
  <div className="rh-card" data-testid="rh-before-after">
    <div className="rh-title">Before · SHA-256 walkthrough evidence &nbsp;|&nbsp; After · Dynamic Variation Studio</div>
    {roomsOf(hub).length === 0 && <div className="rh-note">No wide-angle shots yet. Capture them in the inspection.</div>}
    {roomsOf(hub).map(([rk, label]) => {
      const b = hub.before.find((x) => x.room_key === rk);
      const a = hub.after.find((x) => x.room_key === rk);
      return (
        <div key={rk} className="rh-pair" data-testid={`rh-pair-${rk}`}>
          <div className="rh-pair__label">{label}</div>
          <div className="rh-pair__imgs">
            <figure><img src={`${api.apiBase}${b.url}`} alt={`${label} before`} /><figcaption>Before · {b.sha256.slice(0, 12)}…</figcaption></figure>
            <figure>
              {a ? <img src={`${api.apiBase}${a.after_url}`} alt={`${label} after`} data-testid={`rh-after-${rk}`} />
                 : <div className="rh-empty">{admin ? (
                     <button type="button" className="rh-btn" disabled={busyRoom === rk} onClick={() => onRender(rk)} data-testid={`rh-render-${rk}`}>
                       {busyRoom === rk ? "Rendering (about 30s)…" : "Generate variation render"}
                     </button>) : "Render pending"}</div>}
              <figcaption>After · {a ? a.palette_id.replace(/_/g, " ") : hub.palette_id.replace(/_/g, " ")}</figcaption>
            </figure>
          </div>
        </div>
      );
    })}
  </div>
);

export default function RenderHub({ publicMode = false }) {
  const { inspectionId, token } = useParams();
  const [hub, setHub] = useState(null);
  const [err, setErr] = useState("");
  const [busyRoom, setBusyRoom] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try { setHub(publicMode ? await api.hubPublic(token) : await api.hub(inspectionId)); } catch (e) { setErr(e.message); }
  }, [publicMode, token, inspectionId]);
  useEffect(() => { load(); }, [load]);

  const act = async (name, fn, ok) => {
    setBusy(name);
    try { const r = await fn(); if (ok) ok(r); await load(); } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  const onRender = async (rk) => {
    setBusyRoom(rk);
    try { await api.hubRender(inspectionId, rk, hub.palette_id); toast.success("Variation render ready"); await load(); }
    catch (e) { toast.error(e.message); } finally { setBusyRoom(""); }
  };
  const pdfUrl = publicMode ? `${api.apiBase}/api/public/render-hub/${token}/audit-packet.pdf` : `${api.apiBase}/api/render-hub/${inspectionId}/audit-packet.pdf`;

  if (err) return <div className="rh-page"><div className="rh-card rh-err" data-testid="rh-error">{err}</div></div>;
  if (!hub) return <div className="rh-page"><div className="rh-note" data-testid="rh-loading">Loading Render Hub…</div></div>;
  const i = hub.inspection;

  return (
    <div className="rh-page" data-testid="render-hub-page">
      <header className="rh-head">
        <div>
          <div className="rh-eyebrow">{publicMode ? "Moovin Forward LLC · Private Stabilization Brief" : "Cockpit · Render Hub"}</div>
          <h1 className="rh-h1" data-testid="rh-address">{i.address}{i.unit_label ? ` · Unit ${i.unit_label}` : ""}</h1>
        </div>
        <TierBadge cls={hub.classification} />
      </header>
      <div className="rh-actions">
        {!publicMode && (
          <button type="button" className="rh-btn rh-btn--primary" disabled={!!busy || !!hub.procurement_order} data-testid="rh-approve-po"
                  onClick={() => act("po", () => api.hubApprove(inspectionId), (r) => toast.success(`Procurement order ${r.procurement_order.po_number} issued`))}>
            {hub.procurement_order ? `PO ${hub.procurement_order.po_number} issued` : busy === "po" ? "Issuing…" : "Approve & Issue Procurement Order"}
          </button>
        )}
        <a className="rh-btn" href={pdfUrl} data-testid="rh-download-audit">Download Underwriter & CPA Audit Packet</a>
        {!publicMode && (
          <button type="button" className="rh-btn" disabled={!!busy} data-testid="rh-share-link"
                  onClick={() => act("share", () => api.hubShare(inspectionId), async (r) => {
                    try { await navigator.clipboard.writeText(r.url); toast.success("Private link copied"); } catch { toast.info(r.url); }
                  })}>Share Private Link to Regional / Asset Manager</button>
        )}
        {!publicMode && (
          <select className="rh-select" value={hub.palette_id} data-testid="rh-palette"
                  onChange={(e) => act("pal", () => api.hubPalette(inspectionId, e.target.value))}>
            {hub.palettes.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        )}
      </div>
      <BeforeAfter hub={hub} admin={!publicMode} busyRoom={busyRoom} onRender={onRender} />
      <div className="rh-grid">
        <FinanceCard fin={hub.financials} />
        <CompareCard cmp={hub.comparison} />
      </div>
      <div className="rh-grid">
        <LinesCard lines={hub.procurement_lines} />
        <ComplianceCard hub={hub} />
      </div>
      <footer className="rh-foot">Every before-photo is SHA-256 hashed with GPS + UTC. Protected under Texas CPRC §134A and the Defend Trade Secrets Act.</footer>
    </div>
  );
}
